import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import React, { CSSProperties, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProIconButton } from '../../../../../atoms/Button/ProIconButton';
import { ProCol } from '../../../../../atoms/Grid/Col';
import { ProRow } from '../../../../../atoms/Grid/Row';
import { ProIcon } from '../../../../../atoms/Icons/Icon';
import { ProIconData, ProIconDataButtons } from '../../../../../atoms/Icons/IconData';
import { ProIntent, ProSize } from '../../../../../utils/enums';
import { Components } from '../../../../../utils/types';

type Props = {
    req: Components.Schemas.RequirementDTO;
    addSubtask: (req: Components.Schemas.RequirementDTO) => void;
    selectStory: (id?: string) => void;
    selectTask: (task: Components.Schemas.TaskDTO) => void;
};

export const DailyTaskRow = ({ req, addSubtask, selectStory, selectTask }: Props) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);

    const notStartedTask = req.taskLists
        ?.find((akt) => akt.tasklistType === 'Userlist')
        ?.tasks?.filter((cur) => cur.taskCompletionState?.taskCompletionStateType === 'NotStarted');

    const progressTask = req.taskLists
        ?.find((akt) => akt.tasklistType === 'Userlist')
        ?.tasks?.filter((cur) => cur.taskCompletionState?.taskCompletionStateType === 'InProgress');

    const finishedTask = req.taskLists
        ?.find((akt) => akt.tasklistType === 'Userlist')
        ?.tasks?.filter((cur) => cur.taskCompletionState?.taskCompletionStateType === 'Finished');

    return (
        <ProRow key={`ticket-${req.name}`} className="TaskContainer__row">
            <div className="TaskContainer__story">
                <ProIconButton
                    icon={ProIconData.chevron_outline_down}
                    tooltip={'expand'}
                    mainIntent={ProIntent.Transparent}
                    // hoverIntent={ProIntent.Transpared}
                    size={ProSize.XSmall}
                    lineIntent={ProIntent.Dark}
                    placement="bottom"
                    onClick={() => setExpanded((cur) => !cur)}
                    className={classNames('TaskContainer--expBtn', expanded && 'TaskContainer--expBtn__exp')}
                />
                <ProIcon icon={ProIconData.scope_fill} size={ProSize.Small} />
                <span className="TaskContainer__story_name">{req.name}</span>
                <ProIconButton
                    icon={ProIconDataButtons.add_xs}
                    tooltip={t('buttons.add')}
                    mainIntent={ProIntent.Transparent}
                    hoverIntent={ProIntent.Create}
                    size={ProSize.XSmall}
                    lineIntent={ProIntent.Create}
                    placement="bottom"
                    onClick={() => addSubtask(req)}
                />
                <ProIconButton
                    icon={ProIconDataButtons.arrow_right_xs}
                    tooltip={t('buttons.open')}
                    mainIntent={ProIntent.Transparent}
                    hoverIntent={ProIntent.Secondary}
                    size={ProSize.XSmall}
                    lineIntent={ProIntent.Secondary}
                    placement="bottom"
                    onClick={() => selectStory(req.id)}
                />
            </div>
            <ProRow className={classNames('Daily__tasks-row', expanded && 'Daily__tasks-row__expanded')}>
                <ProCol span={4} className="Daily__task-row">
                    <ul>
                        {notStartedTask?.map((task) => (
                            <TaskDraggable
                                id={task.id ?? ''}
                                selectTask={selectTask}
                                task={task}
                                key={`draggable-task-${task.id}`}
                            />
                        ))}
                    </ul>
                    <Droppable id={`NotStarted-${req.id}`}></Droppable>
                </ProCol>
                <ProCol span={4} className="Daily__task-row">
                    <ul>
                        {progressTask?.map((task) => (
                            <TaskDraggable
                                id={task.id ?? ''}
                                selectTask={selectTask}
                                task={task}
                                key={`draggable-task-${task.id}`}
                            />
                        ))}
                    </ul>
                    <Droppable id={`InProgress-${req.id}`}></Droppable>
                </ProCol>
                <ProCol span={4} className="Daily__task-row">
                    <ul>
                        {finishedTask?.map((task) => (
                            <TaskDraggable
                                id={task.id ?? ''}
                                selectTask={selectTask}
                                task={task}
                                key={`draggable-task-${task.id}`}
                            />
                        ))}
                    </ul>
                    <Droppable id={`Finished-${req.id}`} />
                </ProCol>
            </ProRow>
        </ProRow>
    );
};

const Droppable = ({ id, children }: PropsWithChildren<{ id: string }>) => {
    const { isOver, setNodeRef } = useDroppable({
        id: id,
    });

    const style: CSSProperties = {
        opacity: isOver ? 1 : 0.5,
        background: isOver ? '#9db3d0' : 'none',
        // minHeight: '40px',
        height: '100%',
    };

    return (
        <div ref={setNodeRef} style={style} className="Daily__task-row_dropArea">
            {children}
        </div>
    );
};

const TaskDraggable = ({
    id,
    task,
    selectTask,
}: PropsWithChildren<{
    id: string;
    task: Components.Schemas.TaskDTO;
    selectTask: (task: Components.Schemas.TaskDTO) => void;
}>) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: id,
    });
    const style: CSSProperties = {
        position: isDragging ? 'absolute' : 'relative',
        transform: CSS.Translate.toString(transform),
    };

    const { t } = useTranslation();

    return (
        <button ref={setNodeRef} style={style} className="Daily__task-row_dragButton">
            <li key={`task-${task.name}`}>
                <span {...listeners} {...attributes} className="Daily__task-row_dragRibbon">
                    <ProIcon
                        icon={ProIconData.drag_handle}
                        size={ProSize.Small}
                        intent={ProIntent.PrimaryLight}
                        customHeight="23px"
                        customWidth="15px"
                    />
                </span>
                <ProIcon icon={ProIconData.checkBox_stroke} size={ProSize.Small} />
                <span>
                    <b>{task.name}</b>
                    <br />
                    {task.responsiblePerson?.firstName} {task.responsiblePerson?.lastName}
                </span>
                <div className="TaskContainer__selectIcon">
                    <ProIconButton
                        icon={ProIconDataButtons.arrow_right_xs}
                        tooltip={t('buttons.open')}
                        mainIntent={ProIntent.Transparent}
                        hoverIntent={ProIntent.Secondary}
                        size={ProSize.XSmall}
                        lineIntent={ProIntent.Secondary}
                        placement="bottom"
                        onClick={() => selectTask(task)}
                        small
                    />
                </div>
            </li>
        </button>
    );
};
